import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type { MultiProductPageCF, SubscriptionOfferCF } from '@/types';
import CheckoutStepper from './CheckoutStepper';
import CheckoutView from './CheckoutView';
import { getMockOrderSummary } from './checkoutOrderSummaryData';
import useStyles from './MultiProductPage.styles';
import SignAgreementsView from './SignAgreementsView';
import SubscriptionProductCard from './SubscriptionProductCard';

enum CheckoutStep {
  PRODUCT_SELECTION = 'productSelection',
  AGREEMENTS = 'agreements',
  CHECKOUT = 'checkout',
}

type CheckoutState =
  | { step: CheckoutStep.PRODUCT_SELECTION }
  | { step: CheckoutStep.AGREEMENTS; offer: SubscriptionOfferCF }
  | { step: CheckoutStep.CHECKOUT; offer: SubscriptionOfferCF };

function getStepperStep(state: CheckoutState) {
  switch (state.step) {
    case CheckoutStep.PRODUCT_SELECTION:
      return 1;
    case CheckoutStep.AGREEMENTS:
      return 2;
    case CheckoutStep.CHECKOUT:
      return 3;
    default: {
      const unhandled: never = state;
      return unhandled;
    }
  }
}

interface Props {
  content: MultiProductPageCF;
}

export default function MultiProductPage({
  content: {
    fields: { heading, products },
  },
}: Props) {
  const { classes } = useStyles();
  const [state, setState] = useState<CheckoutState>({
    step: CheckoutStep.PRODUCT_SELECTION,
  });

  const goTo = (next: CheckoutState) => {
    setState(next);
    window.scrollTo({ top: 0 });
  };

  const renderStep = () => {
    switch (state.step) {
      case CheckoutStep.PRODUCT_SELECTION:
        return (
          <>
            {heading && (
              <Typography variant='h2' component='h1'>
                {heading}
              </Typography>
            )}
            <section aria-label={heading} className={classes.products}>
              {products.map(
                (offer) =>
                  offer && (
                    <SubscriptionProductCard
                      key={offer.sys.id}
                      offer={offer}
                      onSelect={() =>
                        goTo({ step: CheckoutStep.AGREEMENTS, offer })
                      }
                    />
                  )
              )}
            </section>
          </>
        );
      case CheckoutStep.AGREEMENTS:
        return (
          <SignAgreementsView
            offer={state.offer}
            onBack={() => goTo({ step: CheckoutStep.PRODUCT_SELECTION })}
            onContinue={() =>
              goTo({ step: CheckoutStep.CHECKOUT, offer: state.offer })
            }
          />
        );
      case CheckoutStep.CHECKOUT:
        return (
          <CheckoutView
            orderSummary={getMockOrderSummary(state.offer)}
            onBack={() =>
              goTo({ step: CheckoutStep.AGREEMENTS, offer: state.offer })
            }
          />
        );
      default: {
        const unhandled: never = state;
        return unhandled;
      }
    }
  };

  return (
    <main className={classes.root}>
      <div className={classes.content}>
        <CheckoutStepper currentStep={getStepperStep(state)} />
        {renderStep()}
      </div>
    </main>
  );
}
